import { useEffect, useMemo, useRef, useState } from "react";
import { auditPrice } from "../utils/priceAuditor";
import { mapProductToVM } from "../utils/mapProductToVM";

export const PricingState = {
  INIT: "INIT",
  LOADING: "LOADING",
  COMPUTING: "COMPUTING",
  READY: "READY",
  ERROR: "ERROR",
};

export function usePricingEngine(produits, profilPricing, userProfil) {
  const [state, setState] = useState(PricingState.INIT);
  const [vmProducts, setVmProducts] = useState([]);
  const runRef = useRef(0);
  const hasReadyOnceRef = useRef(false);

  // 1️⃣ Retouche Lead : Clé de produits robuste (Price-aware)
  const produitsKey = useMemo(() => {
    if (!Array.isArray(produits)) return "none";
    return produits
      .map(
        (p) =>
          `${p.id}-${p.price ?? p.base_price ?? ""}-${p.isDiscount ?? ""}-${
            p.stock ?? ""
          }`
      )
      .join("|");
  }, [produits]);

  // 2️⃣ Retouche Lead : Clés de profil stables (Évite les re-renders infinis sur objets)
  const profilKey = useMemo(
    () =>
      profilPricing
        ? `${profilPricing.id ?? ""}-${profilPricing.updated_at ?? ""}-${
            profilPricing.isAdmin ?? ""
          }`
        : "none",
    [profilPricing]
  );

  const userKey = useMemo(
    () =>
      userProfil
        ? `${userProfil.id ?? ""}-${userProfil.type_profil ?? ""}`
        : "none",
    [userProfil]
  );

  useEffect(() => {
    const runId = ++runRef.current;

    if (!Array.isArray(produits)) {
      setState(PricingState.LOADING);
      return;
    }

    if (produits.length === 0) {
      setVmProducts([]);
      hasReadyOnceRef.current = true;
      setState(PricingState.READY);
      return;
    }

    // Stale-while-revalidate : pas de loader si on a déjà des données
    setState(
      hasReadyOnceRef.current ? PricingState.COMPUTING : PricingState.LOADING
    );

    try {
      // 5️⃣ Retouche Lead : Calcul synchrone protégé
      const computed = produits.map((p) => {
        const audit = auditPrice(p, { profilPricing, userProfil });
        return mapProductToVM(p, audit);
      });

      if (runRef.current !== runId) return;

      setVmProducts(computed);
      hasReadyOnceRef.current = true;
      setState(PricingState.READY);
    } catch (e) {
      if (runRef.current !== runId) return;
      console.error("[PricingEngine] Error:", e);
      setState(PricingState.ERROR);
    }
  }, [produitsKey, profilKey, userKey]);

  return {
    state,
    vmProducts,
    isReady: state === PricingState.READY,
    hasReadyOnce: hasReadyOnceRef.current,
  };
}
